<template>
  <!-- HelpBar -->
  <HelpBar />

  <!--
    Enter springt zum nächsten Text-/Zahlenfeld. Auswahlfelder, mehrzeilige
    Felder und offene Overlays behalten ihre eigene Enter-Bedeutung — siehe
    src/directives/enterAdvance.ts.
  -->
  <div v-if="project && ready" v-enter-advance class="action-view">
    <!-- Method hint alert -->
    <v-alert
      v-if="selectedType?.hint && methodHint"
      type="warning"
      class="action-alert mb-4"
      border="start"
      elevation="2"
      closable
      @update:modelValue="methodHint = $event"
    >
      {{ selectedType.hint }}
    </v-alert>

    <!-- Header / Basic Data Card -->
    <v-card class="action-card" elevation="2">
      <div class="action-card-header">
        <div class="action-title-block">
          <div class="action-title-icon">
            <v-icon color="primary" size="28">mdi-clipboard-edit-outline</v-icon>
          </div>

          <div>
            <div class="text-h6 font-weight-bold">
              {{ updating ? 'Maßnahme bearbeiten' : 'Neue Maßnahme' }}
            </div>
            <div class="text-body-2 text-medium-emphasis">
              Methode, Gruppenbezug und Grunddaten der Maßnahme festlegen.
            </div>
          </div>
        </div>

        <div class="action-header-actions">
          <v-btn
            v-if="selectedType?.hint"
            color="warning"
            variant="tonal"
            class="action-header-button"
            @click="methodHint = true"
          >
            <v-icon start>mdi-alert-outline</v-icon>
            Hinweis
          </v-btn>

          <v-tooltip :disabled="projectStore.sidebarShown" text="Hilfe anzeigen" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                variant="flat"
                class="action-header-button"
                data-help-trigger
                @click="projectStore.sidebarShown ? projectStore.hideSidebar() : projectStore.showSidebar(selectedType?.help ?? NewMethod)"
              >
                <v-icon start>
                  {{ projectStore.sidebarShown ? 'mdi-close' : 'mdi-help-circle-outline' }}
                </v-icon>
                Hilfe
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </div>

      <v-divider />

      <v-card-text class="action-card-body">
        <v-form ref="formHeadRef">
          <v-row>
            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="selectedType"
                :items="selectItems"
                item-title="text"
                item-value="value"
                return-object
                label="Methode"
                :disabled="loading && isLegacyMethod"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-tools"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-text-field
                v-model="name"
                label="Maßnahmenname"
                hint="Name einer einzelnen Maßnahme oder mehrerer gleicher Maßnahmen"
                :error-messages="allErrors?.name"
                validate-on-blur
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-rename-outline"
              />
            </v-col>

            <v-col cols="12" md="4">
              <v-autocomplete
                v-model="group"
                :items="actionGroups"
                item-title="name"
                item-value="id"
                label="Gruppe"
                :disabled="!actionGroups?.length"
                clearable
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-folder-outline"
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>

    <!-- Dynamic Forms -->
    <v-row v-if="selectedType" class="mt-4">
      <!-- Shared form -->
      <v-col v-if="selectedType.sharedForm" cols="12">
        <v-card class="action-section-card" elevation="2">
          <div class="section-card-header">
            <div class="section-header-icon">
              <v-icon color="primary">mdi-link-variant</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Übergreifende Angaben
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Gemeinsame Parameter für Referenz und geplante Maßnahme.
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="section-card-body">
            <component
              ref="formSharedRef"
              :is="selectedType.sharedForm"
              v-model="sharedData"
              :validation-errors="validationErrorsShared"
              :method-value="methodValue"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Reference -->
      <v-col cols="12" md="6">
        <v-card class="action-section-card comparison-card reference-card" elevation="2">
          <div class="section-card-header">
            <div class="section-header-icon">
              <v-icon color="primary">mdi-home-clock-outline</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Referenz
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Ausgangszustand oder bestehende Anlage.
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="section-card-body">
            <component
              ref="formReferenceRef"
              :is="selectedType.form"
              :reference="true"
              v-model="referenceMethod"
              :other-data="plannedMethod"
              :shared-data="sharedData"
              :validation-errors="validationErrorsReference"
              :method-value="methodValue"
            />

            <div class="finance-section">
              <div class="finance-section-title">
                <v-icon color="primary" size="22">mdi-currency-eur</v-icon>
                <span>Finanzen</span>
              </div>

              <v-text-field
                v-model.number="referenceRemovalCost"
                type="number"
                label="Demontagekosten"
                :error-messages="validationErrorsReference?.overwrite_removal_cost"
                suffix="€"
                :hint="Help?.overwrite_removal_cost"
                variant="outlined"
                density="comfortable"
              />

              <v-text-field
                v-model.number="referenceOperationCost"
                type="number"
                label="Betriebs- und Instandhaltungskosten"
                :error-messages="validationErrorsReference?.overwrite_operation_cost"
                suffix="€/a"
                :hint="Help?.overwrite_operation_cost"
                variant="outlined"
                density="comfortable"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Planned -->
      <v-col cols="12" md="6">
        <v-card class="action-section-card comparison-card planned-card" elevation="2">
          <div class="section-card-header">
            <div class="section-header-icon">
              <v-icon color="primary">mdi-rocket-launch-outline</v-icon>
            </div>

            <div>
              <div class="text-h6 font-weight-bold">
                Geplant
              </div>
              <div class="text-body-2 text-medium-emphasis">
                Zielzustand oder geplante Maßnahme.
              </div>
            </div>
          </div>

          <v-divider />

          <v-card-text class="section-card-body">
            <component
              ref="formPlannedRef"
              :is="selectedType.form"
              :planned="true"
              v-model="plannedMethod"
              :other-data="referenceMethod"
              :shared-data="sharedData"
              :validation-errors="validationErrorsPlanned"
              :method-value="methodValue"
            />

            <div class="finance-section">
              <div class="finance-section-title">
                <v-icon color="primary" size="22">mdi-currency-eur</v-icon>
                <span>Finanzen</span>
              </div>

              <v-text-field
                v-model.number="plannedAcquisitionCosts"
                type="number"
                label="Kosten Neuanlage"
                :error-messages="validationErrorsPlanned?.overwrite_acquisition_cost"
                suffix="€"
                :hint="Help?.overwrite_acquisition_cost"
                variant="outlined"
                density="comfortable"
              />

              <v-text-field
                v-model.number="plannedOperationCost"
                type="number"
                label="Betriebs- und Instandhaltungskosten"
                :error-messages="validationErrorsPlanned?.overwrite_operation_cost"
                suffix="€/a"
                :hint="Help?.overwrite_operation_cost"
                variant="outlined"
                density="comfortable"
              />
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Preview -->
    <v-card class="preview-card mt-4" v-if="actionData" elevation="2">
      <div class="preview-card-header">
        <div class="section-header-icon">
          <v-icon color="primary">mdi-chart-box-outline</v-icon>
        </div>

        <div>
          <div class="text-h6 font-weight-bold">
            Vorschau
          </div>
          <div class="text-body-2 text-medium-emphasis">
            Berechnungsrelevante Zusammenfassung der Maßnahme.
          </div>
        </div>
      </div>

      <v-divider />

      <PreviewCard
        :actdata="actionData"
        :actvalid="valid"
        :project-name="project?.name"
        :method-name="name || selectedType?.text"
      />
    </v-card>

    <!-- Footer actions -->
    <v-card class="action-footer-card mt-5" elevation="1">
      <div class="action-footer-content">
        <div class="action-footer-left">
          <DeleteMissileSwitch v-if="updating" @click="onDelete" />

          <v-btn
            v-if="updating"
            variant="tonal"
            color="primary"
            class="footer-button"
            @click="onClone"
          >
            <v-icon start>mdi-content-duplicate</v-icon>
            Duplizieren
          </v-btn>
        </div>

        <div class="action-footer-right">
          <v-btn
            v-if="!validationProtocolNote && validationProtocolButton && isSuperUser"
            color="warning"
            variant="tonal"
            class="footer-button"
            @click="validationProtocolNote = true"
          >
            <v-icon start>mdi-alert-outline</v-icon>
            Fehlerprotokoll
          </v-btn>

          <!-- Error menu on invalid save -->
          <v-menu v-model="menuError" :close-on-content-click="false" location="bottom">
            <template #activator="{ props }">
              <v-btn
                :disabled="!selectedType || saving"
                :loading="saving"
                color="primary"
                variant="elevated"
                class="save-button"
                @click="handleSave"
                v-bind="valid ? {} : props"
              >
                <v-icon start>mdi-content-save</v-icon>
                Speichern
              </v-btn>
            </template>

            <v-list class="save-error-menu">
              <v-list-item>
                <v-list-item-title class="my-2">
                  Eingabe ist nicht vollständig. Bitte überprüfen Sie Ihre Eingabe.
                </v-list-item-title>
              </v-list-item>

              <v-list-item class="px-2">
                <v-btn variant="text" block @click="menuError = false">
                  Bearbeiten
                </v-btn>
              </v-list-item>

              <v-list-item class="px-2">
                <v-btn block color="primary" variant="text" @click="onErrorSubmitDraft">
                  Entwurf speichern
                </v-btn>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>
    </v-card>

    <!-- Fehlerprotokoll for superusers -->
    <v-alert
      v-if="isSuperUser && validationProtocolNote"
      type="warning"
      class="action-alert mt-5"
      border="start"
      elevation="2"
      closable
      @update:modelValue="validationProtocolNote = $event"
    >
      <h3>Fehlerprotokoll:</h3>

      <ul class="mt-3">
        <li v-for="(value, key) in allErrors" :key="`val-${key}`">
          <b>{{ key }}:</b> {{ value }}
        </li>

        <li v-for="(value, key) in validationErrorsShared" :key="`shared-${key}`">
          <b>{{ key }}:</b> {{ value }}
        </li>
      </ul>
    </v-alert>
  </div>
</template>

<script setup>
/* Vue / Router / Pinia */
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useProjectStore } from '@/stores/projectStore'
import { storeToRefs } from 'pinia'

/* Utilities / libs */
import { markRaw } from 'vue'
import api from '@/plugins/axios'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

/* Components (methods, help, misc) */
import DeleteMissileSwitch from '@/components/DeleteMissileSwitch.vue'
import PreviewCard from '@/components/calcpreview/PreviewCard.vue'
import HelpBar from '@/components/HelpBar.vue';

import BoilerForm from '@/components/methods/BoilerForm.vue'
import Lighting1Form from '@/components/methods/Lighting1Form.vue'
import Lighting1FormShared from '@/components/methods/Lighting1FormShared.vue'
import Lighting2Form from '@/components/methods/Lighting2Form.vue'
import Lighting2FormShared from '@/components/methods/Lighting2FormShared.vue'
import RltForm from '@/components/methods/RltForm.vue'
import RltFormShared from '@/components/methods/RltFormShared.vue'
import FreeConsumerForm from '@/components/methods/FreeConsumerForm.vue'
import FreeRenewableForm from '@/components/methods/FreeRenewableForm.vue'
import PVForm from '@/components/methods/PVForm.vue'
import Fan1Form from '@/components/methods/Fan1Form.vue'
import Fan2Form from '@/components/methods/Fan2Form.vue'
import DryPump1Form from '@/components/methods/Legacy/DryPump1Form.vue'
import WetPump1Form from '@/components/methods/Legacy/WetPump1Form.vue'
import Pump1Form from '@/components/methods/Pump1Form.vue'
import Pump1FormShared from '@/components/methods/Pump1FormShared.vue'
import Pump2Form from '@/components/methods/Pump2Form.vue'
import Pump2FormShared from '@/components/methods/Pump2FormShared.vue'
import ElectricDrive1Form from '@/components/methods/ElectricDrive1Form.vue'
import ElectricDrive2Form from '@/components/methods/ElectricDrive2Form.vue'
import ChillerForm from '@/components/methods/ChillerForm.vue'
import FreeGeneratorForm from '@/components/methods/FreeGeneratorForm.vue'
import FreeGeneratorFormShared from '@/components/methods/FreeGeneratorFormShared.vue'

import NewMethod from '@/components/help/NewMethod.vue'
import BoilerHelp from '@/components/help/BoilerHelp.vue'
import ChillerHelp from '@/components/help/ChillerHelp.vue'
import HVACHelp from '@/components/help/HVACHelp.vue'
import Lighting1Help from '@/components/help/Lighting1Help.vue'
import Lighting2Help from '@/components/help/Lighting2Help.vue'
import Fan1Help from '@/components/help/Fan1Help.vue'
import Fan2Help from '@/components/help/Fan2Help.vue'
import WetPump1Help from '@/components/help/Legacy/WetPump1Help.vue'
import DryPump1Help from '@/components/help/Legacy/DryPump1Help.vue'
import Pump1Help from '@/components/help/Pump1Help.vue'
import Pump2Help from '@/components/help/Pump2Help.vue'
import ElectricDrive1Help from '@/components/help/ElectricDrive1Help.vue'
import ElectricDrive2Help from '@/components/help/ElectricDrive2Help.vue'
import FreeConsumerHelp from '@/components/help/FreeConsumerHelp.vue'
import FreeGeneratorHelp from '@/components/help/FreeGeneratorHelp.vue'
import FreeRenewableHelp from '@/components/help/FreeRenewableHelp.vue'
import PVHelp from '@/components/help/PVHelp.vue'
import { useUnsavedChanges } from '@/composables/useUnsavedChanges'

/* Router & store */
const router = useRouter()
const route = useRoute()
const projectStore = useProjectStore()
const { currentProject: project, currentProjectActions: actionList, currentProjectActionGroups: actionGroups, user, ready } = storeToRefs(projectStore)

/* Refs */
const loading = ref(false)

const formSharedRef = ref(null)
const formReferenceRef = ref(null)
const formPlannedRef = ref(null)

const formHeadRef = ref(null)
const name = ref(null)
const selectedType = ref(null)
const group = ref(null)
const generator_type = ref([])

const methodHint = ref(false)
const validationProtocolNote = ref(false)
const validationProtocolButton = ref(false)
const menuError = ref(false)

const referenceMethod = ref({})
const plannedMethod = ref({})
const sharedData = ref({})

// Set while `actionData.set()` ingests a stored Action — see the method-switch watcher.
const ingestingAction = ref(false)

const referenceRemovalCost = ref(null)
const referenceOperationCost = ref(null)
const plannedAcquisitionCosts = ref(null)
const plannedOperationCost = ref(null)

const Help = ref({})

const validationErrors = ref({})
const validationTimer = ref(null)

/* Feldfehler eines abgewiesenen Speicherns — nur zur Anzeige. Getrennt von
   `validationErrors`, weil allein der Prüflauf über `valid` entscheidet; im selben Ref
   würde ein abgewiesener Versuch den Speichern-Knopf verriegeln. */
const saveErrors = ref({})

/* Der Prüflauf läuft ab der ersten Änderung mit, damit `valid` stimmt — angezeigt
   werden seine Feldfehler aber erst, wenn der Nutzer zu speichern versucht hat.
   Sonst färbt sich ein frisch geöffnetes Formular sofort rot: der Prüflauf schickt
   die noch leeren Pflichtfelder an `/actions/validate/` und bekommt für jedes davon
   erwartungsgemäß einen Fehler zurück. Das ist keine Auskunft über die Eingabe des
   Nutzers, sondern über eine Eingabe, die es noch gar nicht gibt. */
const showValidationErrors = ref(false)

/* Beide Quellen für die Anzeige; `saveErrors` wird von jedem Prüflauf geleert.
   `saveErrors` ist immer sichtbar — es entsteht nur durch eine bewusste Aktion. */
const allErrors = computed(() => ({
  ...(showValidationErrors.value ? validationErrors.value : {}),
  ...saveErrors.value,
}))

const saving = ref(false) // to disable save button during save process


// --------------------------
// Mainly computed properties
// --------------------------


/* Helpers */
const actionId = computed(() => {
  const id = route.params.actionId
  return id ? Number(id) : null
})
const updating = computed(() => !!actionId.value)

/* Types (markRaw to avoid proxying) */
const types = [
  { header: 'Beleuchtung' },
  { divider: true },
  {
    text: 'Beleuchtung (überschlägig)',
    value: 'lighting1',
    form: markRaw(Lighting1Form),
    help: markRaw(Lighting1Help),
    hint:
      'Bei der Verwendung dieser Methode sollte beachtet werden, dass die Anlagenparameter stark vereinfacht sind. Deshalb wird bei einer ungewöhnlich niedrigen oder hohen Einsparung empfohlen Ihre Maßnahme mit der ausführlichen Methode gegenzurechnen.',
    sharedForm: markRaw(Lighting1FormShared),
    __typename: 'Lighting1',
  },
  {
    text: 'Beleuchtung',
    value: 'lighting2',
    form: markRaw(Lighting2Form),
    help: markRaw(Lighting2Help),
    sharedForm: markRaw(Lighting2FormShared),
    __typename: 'Lighting2',
  },
  { header: 'Elektrische Antriebe' },
  { divider: true },
  {
    text: 'Elektrische Antriebe (überschlägig)',
    value: 'electricdrive1',
    form: markRaw(ElectricDrive1Form),
    help: markRaw(ElectricDrive1Help),
    hint:
      'Bei der Verwendung dieser Methode sollte beachtet werden, dass die Anlagenparameter stark vereinfacht sind. Deshalb wird bei einer ungewöhnlich hohen Einsparung empfohlen Ihre Maßnahme mit der ausführlichen Methode gegenzurechnen.',
    __typename: 'ElectricDrive1',
  },
  {
    text: 'Elektrische Antriebe',
    value: 'electricdrive2',
    form: markRaw(ElectricDrive2Form),
    help: markRaw(ElectricDrive2Help),
    __typename: 'ElectricDrive2',
  },
  { header: 'Pumpen' },
  { divider: true },
  {
    text: 'Trockenläuferpumpe (überschlägig) – veraltet, bitte „Pumpe“ verwenden',
    value: 'drp1',
    form: markRaw(DryPump1Form),
    help: markRaw(DryPump1Help),
    hint:
      'Veraltete Methode. Für neue Maßnahmen bitte „Pumpe (überschlägig)“ (Trockenläufer-Modus) verwenden. Diese Methode bleibt nur zur Anzeige/Bearbeitung bestehender Maßnahmen verfügbar.',
    __typename: 'DryPump1',
    legacy: true,
  },
  {
    text: 'Nassläuferpumpe (überschlägig) – veraltet, bitte „Pumpe“ verwenden',
    value: 'wrp1',
    form: markRaw(WetPump1Form),
    help: markRaw(WetPump1Help),
    hint:
      'Veraltete Methode. Für neue Maßnahmen bitte „Pumpe (überschlägig)“ (Nassläufer-Modus) verwenden. Diese Methode bleibt nur zur Anzeige/Bearbeitung bestehender Maßnahmen verfügbar.',
    __typename: 'WetPump1',
    legacy: true,
  },
  {
    text: 'Pumpe (überschlägig)',
    value: 'pump1',
    form: markRaw(Pump1Form),
    help: markRaw(Pump1Help),
    hint:
      'Bei der Verwendung dieser Methode sollte beachtet werden, dass die Anlagenparameter stark vereinfacht sind. Deshalb wird bei einer ungewöhnlich hohen Einsparung empfohlen Ihre Maßnahme mit der ausführlichen Methode gegenzurechnen.',
    sharedForm: markRaw(Pump1FormShared),
    __typename: 'Pump1',
  },
  {
    text: 'Pumpe',
    value: 'pump2',
    form: markRaw(Pump2Form),
    help: markRaw(Pump2Help),
    hint:
      '',
    sharedForm: markRaw(Pump2FormShared),
    __typename: 'Pump2',
  },
  { header: 'Ventilatoren + RLT' },
  { divider: true },
  {
    text: 'RLT',
    value: 'hvac',
    form: markRaw(RltForm),
    help: markRaw(HVACHelp),
    sharedForm: markRaw(RltFormShared),
    __typename: 'HVAC',
  },
  {
    text: 'Ventilator (überschlägig)',
    value: 'fan1',
    form: markRaw(Fan1Form),
    help: markRaw(Fan1Help),
    hint:
      'Bei der Verwendung dieser Methode sollte beachtet werden, dass die Anlagenparameter stark vereinfacht sind. Deshalb wird bei einer ungewöhnlich hohen Einsparung empfohlen Ihre Maßnahme mit der ausführlichen Methode gegenzurechnen.',
    __typename: 'Fan1',
  },
  {
    text: 'Ventilator',
    value: 'fan2',
    form: markRaw(Fan2Form),
    help: markRaw(Fan2Help),
    __typename: 'Fan2',
  },
  { header: 'Erzeuger' },
  { divider: true },
  {
    text: 'Heizkessel',
    value: 'boiler',
    form: markRaw(BoilerForm),
    help: markRaw(BoilerHelp),
    __typename: 'Boiler',
  },
  {
    text: 'Kältemaschine',
    value: 'chiller',
    form: markRaw(ChillerForm),
    help: markRaw(ChillerHelp),
    __typename: 'Chiller',
  },
  { header: 'Sonstiges' },
  { divider: true },
  {
    text: 'Sonstiger Verbraucher',
    value: 'free-con',
    form: markRaw(FreeConsumerForm),
    help: markRaw(FreeConsumerHelp),
    __typename: 'FreeConsumer',
  },
  {
    text: 'Sonstiger Erzeuger',
    value: 'freegen',
    form: markRaw(FreeGeneratorForm),
    sharedForm: markRaw(FreeGeneratorFormShared),
    help: markRaw(FreeGeneratorHelp),
    __typename: 'FreeGenerator',
  },
  {
    text: 'Sonstige erneuerbare Energien',
    value: 'freeren',
    form: markRaw(FreeRenewableForm),
    help: markRaw(FreeRenewableHelp),
    __typename: 'FreeRenewable',
  },
  {
    text: 'Photovoltaik',
    value: 'pv',
    form: markRaw(PVForm),
    help: markRaw(PVHelp),
    __typename: 'PV',
  },
]


/*
 * Every supported method, including legacy methods.
 * Used when loading existing actions.
 */
const allMethodItems = computed(() => {
  return types.filter(item => item.value)
})

/*
 * Methods displayed in the selector.
 *
 * Legacy methods are hidden for new actions. When editing an
 * existing legacy action, its current method remains available
 * so the select can display the existing value correctly.
 */
const selectItems = computed(() => {
  return allMethodItems.value.filter(item => {
    if (!item.legacy) {
      return true
    }

    return (
      updating.value &&
      selectedType.value?.value === item.value
    )
  })
})

const isLegacyMethod = computed(() => {
  return selectedType.value?.legacy === true
})

/* Filtered items for the v-select (skip headers/dividers) */
// const selectItems = computed(() => types.filter(t => t.value))

/* Help sidebar button (like your example) */

/* Computed helpers */
const methodValue = computed(() =>
  (selectedType.value?.__typename ?? '').toLowerCase()
)

/* Method key. Could be repladed with methodKey inside of actionData */

/* Validation errors (scoped) */
const validationErrorsReference = computed(() => allErrors.value?.reference || {})
const validationErrorsPlanned = computed(() => allErrors.value?.planned || {})

/* Shared errors: gather fields from shared form instance (defineExpose({ fields })) */
const validationErrorsShared = computed(() => {
  const fields = formSharedRef.value?.fields || []
  if (!fields.length) return {}
  // Include top-level errors: a shared field is rejected by the Action serializer itself
  // (e.g. "Bestand und Planung müssen dieselbe Berechnungsmethode verwenden"), not by either
  // nested method serializer, so it arrives unnested.
  const all = {
    ...validationErrorsPlanned.value,
    ...validationErrorsReference.value,
    ...(allErrors.value || {}),
  }
  return fields.reduce((acc, key) => {
    if (all[key]) acc[key] = all[key]
    return acc
  }, {})
})

/* Two-way computed payload */
const actionData = computed({
  get() {
    if (!selectedType.value || !project.value) {
      return null
    }

    const methodKey = selectedType.value.value
    const methodTypeName = selectedType.value.__typename

    // Shared fields (e.g. calculation_method) select HOW the method calculates. An Action is a
    // comparison, so both variants must use the same value — the backend rejects a mismatch.
    // sharedData aliases the planned object, so without this the reference side would keep its
    // previously stored mode when the user switches the selector.
    const sharedFieldKeys = formSharedRef.value?.fields || []
    const sharedValues = sharedFieldKeys.reduce((acc, key) => {
      if (sharedData.value?.[key] !== undefined) {
        acc[key] = sharedData.value[key]
      }
      return acc
    }, {})

    return {
      name: name.value,
      group: group.value,
      project: project.value.id,
      generator_type: generator_type.value,

      // Action-level method identifier
      method: methodKey,

      reference: {
        ...referenceMethod.value,

        // Shared selection wins over whatever this side had stored before
        ...sharedValues,

        // Required by the nested reference serializer
        method_key: methodKey,

        __typename: methodTypeName,
        is_reference: true,

        overwrite_operation_cost:
          referenceOperationCost.value,

        overwrite_removal_cost:
          referenceRemovalCost.value,
      },

      planned: {
        ...plannedMethod.value,

        // Shared selection wins over whatever this side had stored before
        ...sharedValues,

        // Required by the nested planned serializer
        method_key: methodKey,

        __typename: methodTypeName,
        is_reference: false,

        overwrite_operation_cost:
          plannedOperationCost.value,

        overwrite_acquisition_cost:
          plannedAcquisitionCosts.value,
      },
    }
  },

  set(action) {
    // Loading a stored Action assigns `selectedType` and the two method payloads together, and
    // that payload belongs to that method. Suppress the method-switch reset below for this tick
    // so it does not wipe the values we are in the middle of ingesting.
    ingestingAction.value = true
    nextTick(() => { ingestingAction.value = false })

    if (project.value?.id !== action.project) {
      alert(
        'critical error: action not from expected project.'
      )
    }

    name.value = action.name
    group.value = action.group

    /*
     * Existing actions may expose their method key through:
     * - action.method
     * - action.method_key
     * - reference.method_key
     * - planned.method_key
     */
    const methodKey =
      action.method ??
      action.method_key ??
      action.reference?.method_key ??
      action.planned?.method_key ??
      null
    
    selectedType.value =
      allMethodItems.value.find(
          item => item.value === methodKey
        ) ?? null

    // selectedType.value =
    //   selectItems.value.find(
    //     item => item.value === methodKey
    //   ) ?? null

    const reference = action.partial_ref
      ? JSON.parse(action.partial_ref)
      : action.reference

    const planned = action.partial_plan
      ? JSON.parse(action.partial_plan)
      : action.planned

    if (Array.isArray(action.generator_type)) {
      generator_type.value = [
        ...action.generator_type,
      ]
    } else if (
      Array.isArray(action.generator_type?.value)
    ) {
      generator_type.value = [
        ...action.generator_type.value,
      ]
    } else {
      generator_type.value = []
    }

    if (reference) {
      reference.is_reference = true
    }

    if (planned) {
      planned.is_reference = false
    }

    referenceMethod.value = reference ?? {}
    plannedMethod.value = planned ?? {}
    sharedData.value = planned ?? {}

    referenceOperationCost.value =
      reference?.overwrite_operation_cost ?? null

    referenceRemovalCost.value =
      reference?.overwrite_removal_cost ?? null

    plannedOperationCost.value =
      planned?.overwrite_operation_cost ?? null

    plannedAcquisitionCosts.value =
      planned?.overwrite_acquisition_cost ?? null
  },
})



// const actionData = computed({ 
//   get() {
//     if (!selectedType.value || !project.value) return null
//     return {
//       name: name.value,
//       group: group.value,
//       project: project.value.id,
//       generator_type: generator_type.value,
//       method: selectedType.value.value,
//       reference: {
//         ...referenceMethod.value,
//         __typename: selectedType.value.__typename,
//         overwrite_operation_cost: referenceOperationCost.value,
//         overwrite_removal_cost: referenceRemovalCost.value,
//       },
//       planned: {
//         ...plannedMethod.value,
//         __typename: selectedType.value.__typename,
//         overwrite_operation_cost: plannedOperationCost.value,
//         overwrite_acquisition_cost: plannedAcquisitionCosts.value,
//       },
//     }
//   },
//   set(action) {
//     if (project.value?.id !== action.project) {
//       alert('critical error: action not from expected project.')
//     }

//     name.value = action.name
//     group.value = action.group

//     selectedType.value = selectItems.value.find(m => m.value === action.method) || null

//     const reference = action.partial_ref ? JSON.parse(action.partial_ref) : action.reference
//     const planned = action.partial_plan ? JSON.parse(action.partial_plan) : action.planned
    
//     generator_type.value = action.generator_type.value
//     reference.is_reference = true
//     planned.is_reference = false


//     referenceMethod.value = reference
//     plannedMethod.value = planned
//     sharedData.value = planned

//     referenceOperationCost.value = reference?.overwrite_operation_cost ?? null
//     referenceRemovalCost.value = reference?.overwrite_removal_cost ?? null
//     plannedOperationCost.value = planned?.overwrite_operation_cost ?? null
//     plannedAcquisitionCosts.value = planned?.overwrite_acquisition_cost ?? null
//   },
// })

/* Valid state */
const valid = computed(() =>
  Object.keys(validationErrors.value || {}).length === 0 &&
  !validationTimer.value &&
  !!selectedType.value &&
  !!name.value
)

/* Watchers */
watch(actionData, (val) => {
  if (val) scheduleValidate()
})

watch(
  () => route.fullPath,
  () => { fetchAll() },
  { immediate: true }
)

watch(project, (p) => {
  if (p) projectStore.setTitle(p.name ?? null)
}, { immediate: true })

/* Lifecycle */
onMounted(async () => {
  await projectStore.init()
  await fetchIsSuperUser()
})



// --------------------------
// Mainly functions
// --------------------------


/* Fetch combined */
// Function to fetch projectid and action data
/*
 * Warnung vor ungespeicherten Eingaben.
 *
 * Verglichen wird `actionData` — dieselbe Nutzlast, die beim Speichern an den
 * Server geht. Gemerkt wird nach dem Laden und nach jedem erfolgreichen
 * Speichern, damit das Speichern selbst beim Zurückblättern nicht warnt.
 */
const { merkeStand } = useUnsavedChanges(() => actionData.value)

async function fetchAll() {
  try {
    loading.value = true
    const projectId = Number(route.params.projectId)
    await projectStore.fetchCurrentProject(projectId)
    if (updating.value) {
      const { data } = await api.get(`/actions/${actionId.value}/`)
      actionData.value = data
    }
    await fetchHelp()
    // Der geladene Stand gilt als gespeichert; bei einer neuen Maßnahme sind
    // es die Vorgabewerte.
    merkeStand()
  } finally {
    loading.value = false
  }
}

/* Financial help per method */
// Returns the help texts depending on teh selected method
async function fetchHelp() {
  if (!selectedType.value) {
    Help.value = {}
    return
  }
  const key = selectedType.value.__typename?.toLowerCase?.()
  if (!key) {
    Help.value = {}
    return
  }
  try {
    const { data } = await api.get(`/methods/${key}/help/`)
    Help.value = Object.assign({}, ...data.map(x => ({ [x.key]: x.help })))
  } catch {
    Help.value = {}
  }
}
watch(selectedType, fetchHelp)

/*
 * Changing the Action's method must discard the previous method's field values.
 *
 * `referenceMethod` / `plannedMethod` / `sharedData` are plain backend-shaped payloads, and
 * different methods reuse the same backend field name for incompatible things. `regulation` is the
 * sharpest case: on Pump1 it is 0–3 (0 = ungeregelt), on Pump2 it is 1–4. Carrying the payload
 * across a switch therefore posts a code the new method rejects — and the offending field is
 * invisible, because a value outside the picker's item list renders as an empty select. The
 * reported symptom was a Pump1 -> Pump2 switch failing with
 * `regulation: "0" ist keine gültige Option.` once a valid Berechnungsmethode was chosen.
 *
 * `calculation_method` itself collides the same way (Pump1's modes are not Pump2's), so the shared
 * payload is reset too; each shared form then re-applies its own default.
 */
watch(selectedType, (next, prev) => {
  if (ingestingAction.value) return
  if (!prev || next?.value === prev.value) return

  referenceMethod.value = {}
  plannedMethod.value = {}
  sharedData.value = {}

  // Anderes Verfahren, anderes Formular: die Fehler des vorigen gelten nicht mehr.
  showValidationErrors.value = false
  saveErrors.value = {}
})

/* Validation (debounced POST to /validate/) */
function scheduleValidate() {
  if (validationTimer.value) clearTimeout(validationTimer.value)
  validationTimer.value = setTimeout(async () => {
    validationTimer.value = null
    try {
      await api.post('/actions/validate/', actionData.value)
      validationErrors.value = {}
      // Der Prüflauf ist die frischere Auskunft.
      saveErrors.value = {}
    } catch (err) {
      const status = err?.response?.status
      if (status === 400) {
        // Kein Log: der Prüflauf schlägt auf einem noch leeren Formular
        // erwartungsgemäß fehl und würde die Konsole dauerhaft zumüllen.
        validationErrors.value = err.response.data || {}
        saveErrors.value = {}
      }
    }
  }, 600)
}

/* Clone helpers */
function sanitize(obj) {
  const out = { ...obj }
  Object.keys(out).forEach(k => out[k] === undefined && delete out[k])
  return out
}


// Sub function to clone generator actions in the backend
async function cloneGeneratorPostRequest(plannedGen, referenceGen, groupId) {
  const grp = actionGroups.value?.find(g => g.id === groupId)
  if (!grp) throw new Error('Group not found')


  let newPlannedGen = plannedGen
  let newReferenceGen = referenceGen

  const ops = []

  if (plannedGen && plannedGen.group !== groupId) {
    const payload = sanitize({
      ...plannedGen,
      group: groupId,
      name: `${plannedGen.name} (${grp.name})`,
      project: project.value.id,
      
      method_key:
        plannedGen.method_key ??
        plannedGen.method,

      id: undefined,
    })
    ops.push(api.post('/actions/', payload).then(r => { newPlannedGen = r.data }))
  }

  if (referenceGen && referenceGen.group !== groupId && plannedGen?.id !== referenceGen?.id) {
    const payload = sanitize({
      ...referenceGen,
      group: groupId,
      name: `${referenceGen.name} (${grp.name})`,
      project: project.value.id,

      method_key:
        referenceGen.method_key ??
        referenceGen.method,

      id: undefined,
    })
    ops.push(api.post('/actions/', payload).then(r => { newReferenceGen = r.data }))
  }

  if (ops.length > 0) {
    await Promise.all(ops)
    toast.success(`Erfolgreich ${ops.length} Generator(en) dupliziert.`)
  }

  return [newPlannedGen, newReferenceGen]
}


// sub function to clone generator actions if needed
async function cloneGenerators(plannedData, referenceData) {
  if (!group.value) return [plannedData, referenceData]

  let plannedGen = null
  let referenceGen = null

  if (plannedData?.action_id) {
    plannedGen = actionList.value?.find(a => a.id === plannedData.action_id) || null
  }
  if (referenceData?.action_id) {
    referenceGen = actionList.value?.find(a => a.id === referenceData.action_id) || null
  }

  const needsClone =
    (plannedGen && plannedGen.group !== group.value) ||
    (referenceGen && referenceGen.group !== group.value)

  if (!needsClone) return [plannedData, referenceData]

  const [newPlannedGen, newReferenceGen] = await cloneGeneratorPostRequest(plannedGen, referenceGen, group.value)

  if (plannedData) plannedData.action_id = newPlannedGen?.id
  if (referenceData) referenceData.action_id = newReferenceGen?.id

  return [plannedData, referenceData]
}

// sub function to clone generator actions if needed
async function cloneColdGens(plannedData, referenceData) {
  if (!group.value) return [plannedData, referenceData]

  let plannedGen = null
  let referenceGen = null

  if (plannedData?.coldgen_name) {
    plannedGen = actionList.value?.find(a => a.id === plannedData.coldgen_name) || null
  }
  if (referenceData?.coldgen_name) {
    referenceGen = actionList.value?.find(a => a.id === referenceData.coldgen_name) || null
  }

  const needsClone =
    (plannedGen && plannedGen.group !== group.value) ||
    (referenceGen && referenceGen.group !== group.value)

  if (!needsClone) return [plannedData, referenceData]

  const [newPlannedGen, newReferenceGen] = await cloneGeneratorPostRequest(plannedGen, referenceGen, group.value)

  if (plannedData) plannedData.coldgen_name = newPlannedGen?.id
  if (referenceData) referenceData.coldgen_name = newReferenceGen?.id

  return [plannedData, referenceData]
}

/* Footer actions */
// Functios to save actions 
function handleSave() {
  /*
   * Ab dem ersten Speicherversuch sind die Feldfehler eine Antwort auf eine
   * Handlung des Nutzers und dürfen gezeigt werden — gerade dann, wenn der
   * Versuch wegen fehlender Pflichtfelder gar nicht erst abgeschickt wird.
   */
  showValidationErrors.value = true

  if (valid.value) {
    onSubmit()
    return
  }

  /*
   * Vorher verpuffte der Klick auf einem unvollständigen Formular wirkungslos
   * (`valid ? handleSave() : null`): kein Speichern, keine Meldung, kein Hinweis
   * worauf zu achten ist.
   */
  validationProtocolButton.value = true
  menuError.value = true
  toast.error('Bitte prüfen Sie die markierten Felder.')
}

async function onSubmit() {
  if (saving.value) return
  saving.value = true
  try {
    let data = { ...actionData.value }
    data.partial_plan = null
    data.partial_ref = null

    generator_type.value = []; // Reset generator_type array

    // Clone generators if needed
    const [clonePlannedData, cloneReferenceData] = await cloneGenerators(
      plannedMethod.value,
      referenceMethod.value
    );

    // Clone cold gens if needed
    const [newPlannedData, newReferenceData] = await cloneColdGens(
      clonePlannedData,
      cloneReferenceData
    );

    // Update methods with new data   
    newPlannedData.is_reference = false
    newReferenceData.is_reference = true

    plannedMethod.value = newPlannedData
    referenceMethod.value = newReferenceData

    // Update generator_type (keep duplicates like original behavior)
    if (newPlannedData?.action_id) generator_type.value.push(newPlannedData.action_id)
    if (newReferenceData?.action_id) generator_type.value.push(newReferenceData.action_id)
    if (newPlannedData?.coldgen_name) generator_type.value.push(newPlannedData.coldgen_name)
    if (newReferenceData?.coldgen_name) generator_type.value.push(newReferenceData.coldgen_name)

    // Refresh payload
    data = { ...actionData.value }
    data.partial_plan = null
    data.partial_ref = null


    const promise = updating.value
      ? api.put(`/actions/${actionId.value}/`, data)
      : api.post('/actions/', data)

    promise
      .then((response) => {
        if (updating.value) {
          toast.success(`Maßnahme <b>${response.data.name}</b> erfolgreich gespeichert.`, { dangerouslyHTMLString: true })
        } else {
          toast.success(`Maßnahme <b>${response.data.name}</b> erfolgreich erstellt.`, { dangerouslyHTMLString: true })
        }
        merkeStand()
        router.go(-1)
      })
      // `error` als Parameter: sonst ist es der Bezeichner des äußeren `catch` und hier
      // ungebunden — ein ReferenceError ließ das abgewiesene Speichern still scheitern.
      .catch((error) => {
        const data = error?.response?.data
        console.error('ACTION SAVE ERROR.', data ?? error)

        // Nur ein 400 trägt die Feldfehler von DRF; ein 500 oder eine abgerissene
        // Verbindung hat keine Feldzuordnung und würde Servermeldungen an die Eingaben
        // schreiben. Gezeigt, nicht gesperrt: `saveErrors` ist reine Anzeige, damit ein
        // abgewiesener Versuch den Speichern-Knopf nicht verriegelt.
        if (error?.response?.status === 400 && data && typeof data === 'object') {
          saveErrors.value = data
        } else {
          toast.error('Fehler beim Speichern.')
        }

        validationProtocolButton.value = true
        // Ask child forms to validate themselves, if they expose methods
        formSharedRef.value?.validateForm?.()
        formReferenceRef.value?.validateForm?.()
        formPlannedRef.value?.validateForm?.()
        menuError.value = true
      })
  } catch {
    toast.error('Fehler beim Speichern.')
  } finally {
    saving.value = false
  }
}


// functions to submit draft when there is an error
function onErrorSubmitDraft() {
  onSubmitDraft()
  menuError.value = false
}

function onSubmitDraft() {
  const data = { ...actionData.value }
  data.partial_plan = JSON.stringify(data.planned)
  data.partial_ref = JSON.stringify(data.reference)
  delete data.planned
  delete data.reference

  const promise = updating.value
    ? api.put(`/actions/${actionId.value}/`, data)
    : api.post('/actions/', data)

  promise
    .then((response) => {
      if (updating.value) {
        toast.warn(`Maßnahme <b>${response.data.name}</b> erfolgreich als Entwurf gespeichert.`, { dangerouslyHTMLString: true })
      } else {
        toast.warn(`Maßnahme <b>${response.data.name}</b> erfolgreich als Entwurf erstellt.`, { dangerouslyHTMLString: true })
      }
      merkeStand()
    })
    .catch(() => {
      // Trigger field validations if available
      formSharedRef.value?.validateForm?.()
      formReferenceRef.value?.validateForm?.()
      formPlannedRef.value?.validateForm?.()
      toast.error('Es gab einen Fehler beim Erstellen/Speichern des Maßnahmenentwurfs.')
    })
}

// function to delete action from backend
function onDelete() {
  api
    .delete(`/actions/${actionId.value}/`, { data: actionData.value })
    .then(() => {
      toast.success(`Maßnahme <b>${name.value}</b> erfolgreich gelöscht.`, { dangerouslyHTMLString: true })
      // Gelöscht — es gibt nichts mehr zu speichern, wovor zu warnen wäre.
      merkeStand()
      router.go(-1)
    })
    .catch(() => {
      toast.error('Es gab einen Fehler beim Löschen der Maßnahme.')
    })
}


// function to clone non draft action from backend
function onClone() {
  const data = { ...actionData.value }
  data.partial_plan = null
  data.partial_ref = null

  api
    .post('/actions/', data)
    .then(() => {
      toast.success(`Maßnahme <b>${name.value}</b> erfolgreich dupliziert.`, { dangerouslyHTMLString: true })
      router.go(-1)
    })
    .catch(() => {
      toast.error('Es gab einen Fehler beim Duplizieren der Maßnahme.')
    })
}

/* Superuser check (if not available on user) */
async function fetchIsSuperUser() {
  try {
    const { data } = await api.get('/superuser/')
    isSuperUser.value = !!data?.is_superuser
  } catch {
    isSuperUser.value = !!(user.value?.is_superuser)
  }
}

/* Superuser flag */
const isSuperUser = ref(false)
watch(user, (u) => {
  if (u && typeof u.is_superuser !== 'undefined') {
    isSuperUser.value = !!u.is_superuser
  }
}, { immediate: true })
</script>

<style scoped lang="scss">
.action-view {
  padding-bottom: 1rem;
}

.action-alert {
  border-radius: 22px;
}

/* Main action card */
.action-card,
.action-section-card,
.preview-card,
.action-footer-card {
  overflow: hidden;
  border-radius: 28px;
  background-color: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.action-card-header,
.section-card-header,
.preview-card-header {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1.25rem 1.5rem;
  background:
    linear-gradient(
      180deg,
      rgba(var(--v-theme-primary), 0.07),
      rgba(var(--v-theme-primary), 0.025)
    );
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.action-card-header {
  justify-content: space-between;
}

.action-title-block {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 260px;
}

.action-title-icon,
.section-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: rgba(var(--v-theme-primary), 0.08);
}

.action-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.action-header-button,
.footer-button,
.save-button {
  text-transform: none;
  font-weight: 600;
}

.action-card-body,
.section-card-body {
  padding: 1.25rem 1.5rem 1.5rem;
}

/* Child form integration */
:deep(.v-field) {
  border-radius: 18px;
}

:deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Comparison cards */
.comparison-card {
  height: 100%;
}

.reference-card {
  box-shadow: inset 4px 0 0 rgba(var(--v-theme-secondary), 0.45);
}

.planned-card {
  box-shadow: inset 4px 0 0 rgba(var(--v-theme-primary), 0.5);
}

/* Finance mini section */
.finance-section {
  margin-top: 1.25rem;
  padding: 1rem;
  border-radius: 22px;
  background-color: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.08);
}

.finance-section-title {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
}

/* Preview */
.preview-card {
  margin-top: 1rem;
}

/* Footer */
.action-footer-card {
  background-color: rgb(var(--v-theme-surface));
}

.action-footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
}

.action-footer-left,
.action-footer-right {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.save-button {
  min-width: 150px;
}

.save-error-menu {
  border-radius: 18px;
}

/* Old utility, preserved if used somewhere */
.scroll {
  overflow-y: scroll;
  padding: 15px;
  resize: vertical;
}

/* Responsive */
@media (max-width: 1100px) {
  .action-card-header {
    align-items: stretch;
    flex-direction: column;
  }

  .action-header-actions {
    justify-content: flex-start;
  }

  .action-footer-content {
    align-items: stretch;
    flex-direction: column;
  }

  .action-footer-left,
  .action-footer-right {
    justify-content: flex-start;
  }
}

@media (max-width: 700px) {
  .action-card-header,
  .section-card-header,
  .preview-card-header {
    align-items: flex-start;
    padding: 1rem;
  }

  .action-card-body,
  .section-card-body {
    padding: 1rem;
  }

  .action-title-icon,
  .section-header-icon {
    width: 42px;
    height: 42px;
  }

  .action-header-actions,
  .action-footer-left,
  .action-footer-right {
    align-items: stretch;
    flex-direction: column;
  }

  .action-header-actions .v-btn,
  .action-footer-left .v-btn,
  .action-footer-right .v-btn {
    width: 100%;
  }

  .save-button {
    width: 100%;
  }
}
</style>
